<template>
  <div class="memlist-select-input-container ml-select-input2">
    <div v-if="layout === 'horizontal'" class="horizontal-layout">
      <div v-if="title" class="horizontal1">
        <label class="ml-text-label" style="font-weight: 500;">
          {{ title }}
          <span v-if="required" class="required-asterisk">*</span>
        </label>
      </div>
      <div class="horizontal2">
        <select 
          :disabled="disabled" 
          v-model="local_value" 
          :id="`memlist-select-${name}`"
          :style="selectStyle"
          :class="[
            sizeClass,
            state === true ? 'success1' : state === false ? 'failure1' : '',
          ]"
        >
          <option v-if="show_none" :value="null">{{ $t('PAGES.ATTRIBUTES.NONE') }}</option>
          <option
            v-for="prop of items"
            :key="prop.value"
            :value="prop.value"
            :style="prop.color ? { color: prop.color } : {}"
          >{{ prop.text }}</option>
        </select>

        <div class="error-text" v-if="required && invalid_text && invalid_text.length > 0 && local_state === false">{{ invalid_text }}</div>
        <div class="info-text">{{ info_text }}</div>
      </div>
    </div>

    <div v-if="layout === 'vertical'" class="vertical-layout">
      <div v-if="title" class="vertical1">
        <label class="ml-text-label" style="font-weight: 500;">
          {{ title }}
          <span v-if="required" class="required-asterisk">*</span>
        </label>
      </div>
      <div class="vertical2">
        <select 
          :disabled="disabled" 
          v-model="local_value" 
          :id="`memlist-select-${name}`"
          :style="selectStyle"
          :class="[
            sizeClass,
            state === true ? 'success1' : state === false ? 'failure1' : '',
          ]"
        >
          <option v-if="show_none" :value="null">{{ $t('PAGES.ATTRIBUTES.NONE') }}</option>
          <option
            v-for="prop of items"
            :key="prop.value"
            :value="prop.value"
            :style="prop.color ? { color: prop.color } : {}"
          >{{ prop.text }}</option>
        </select>

        <div class="error-text" v-if="required && invalid_text && invalid_text.length > 0 && local_state === false">{{ invalid_text }}</div>
        <div class="info-text">{{ info_text }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { toasts } from '@/core/mixins/toastr-helper.mixin.js';
import { validate_email } from '@/core/services/email';
import { validatePersonnr } from '@/core/services/personnr';

export default {
  name: 'MemlistSelectInput2',

  props: {
    layout: {
      type: String,
      default: 'horizontal',
    },
    item: {
      type: Object,
      default: null,
    },
    show_none: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    validate: {
      type: Number,
      default: 0,
    },
    invalid_text: {
      type: String,
      default: '',
    },
    info_text: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    validation_type: {
      type: String,
      default: 'TEXT',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    value: [String, Number, Boolean], // For Vue 2
    placeholder: {
      type: String,
      default: '',
    },
    validation_function: {
      type: Function,
      default: null,
    },
    state: {
      type: Boolean,
      default: null,
    },
    size: {  // Add size prop to control the size of the select input
      type: String,
      default: 'sm', // Default size is medium
      validator(value) {
        // Ensure valid values: 'sm', 'md', 'lg'
        return ['sm', 'md', 'lg'].includes(value);
      }
    }
  },

  mixins: [toasts],

  emits: ['input', 'validated'],

  data() {
    return {
      locked: false,
      local_state: null,
      local_value: this.value,
    };
  },

  computed: {
    // Dynamically set the size class based on the size prop
    sizeClass() {
      return {
        sm: 'size-sm',
        md: 'size-md',
        lg: 'size-lg',
      }[this.size] || 'size-md'; // Default to 'md' if size is not defined
    },
    selectStyle() {
      const selectedItem = (this.items || []).find(item => item.value === this.local_value);
      return selectedItem && selectedItem.color ? { color: selectedItem.color } : {};
    },

    state_class() {
      if (!this.required) {
        return 'memlist-text-input';
      }

      if (this.local_state !== null) {
        return this.local_state ? 'memlist-text-input success1' : 'memlist-text-input failure1';
      }

      return 'memlist-text-input';
    },
  },

  watch: {
    local_value(newValue) {
      if (this.locked) {
        return;
      }
      this.$emit('input', newValue, this.item);
    },
    validate(newValue) {
      if (newValue !== 0) {
        this.local_state = this.validate_input();
      }
    },
    value(newValue) {
      this.local_value = newValue;
    },
  },

  mounted() {
    this.locked = true;
    this.local_value = this.value;
    this.locked = false;
  },

  methods: {
    validate_input() {
      if (this.validation_function !== null) {
        const res = this.validation_function(this.local_value);

        this.$emit('validated', this.name, this.local_value, res, this.item);

        this.local_state = res;

        return res;
      }

      if (!this.required) {
        return true;
      }

      if (this.local_value === undefined) {
        this.local_value = '';
      }

      if (this.disabled) {
        this.$emit('validated', this.name, this.local_value, true, this.item);
        return true;
      }

      if (this.required && !this.local_value) {
        this.$emit('validated', this.name, this.local_value, false, this.item);
        return false;
      }

      if (this.validation_type === 'TEXT') {
        const res = this.local_value.length > 0;

        this.$emit('validated', this.name, this.local_value, res, this.item);

        return res;
      } else if (this.validation_type === 'PERSONNR') {
        const validated = validatePersonnr(this.local_value);

        if (validated.valid) {
          this.local_value = validated.formatted;

          this.$emit('validated', this.name, this.local_value, true, this.item);
          return true;
        } else {
          this.$emit('validated', this.name, this.local_value, false, this.item);
          return false;
        }
      } else if (this.validation_type === 'DOB') {
        const validated = validatePersonnr(this.local_value);

        if (validated.success) {
          this.local_value = validated.birthdate;

          this.$emit('validated', this.name, this.local_value, true, this.item);
          return true;
        } else {
          this.$emit('validated', this.name, this.local_value, false, this.item);
          return false;
        }
      } else if (this.validation_type === 'EMAIL') {
        const res = validate_email(this.local_value);

        this.$emit('validated', this.name, this.local_value, res, this.item);

        return res;
      }

      this.$emit('validated', this.name, this.local_value, true, this.item);
      return true;
    },

    on_input(event) {
      this.local_state = this.validate_input();
      this.$emit('input', this.local_value, this.item);
    },
  },
};
</script>

<style scoped>

.ml-text-label {
  font-size: 13px;
}

/* Default styles for select */
select {
  width: 100%;
  font-size: 13px;
  padding: 8px 12px;
  border: 1px solid #e1e1e1;
  border-radius: 6px;
  background: url("data:image/svg+xml;charset=UTF8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23999' viewBox='0 0 24 24'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  background-size: 16px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  box-sizing: border-box;
}

/* Size classes */
.size-sm {
  font-size: 12px;
  padding: 6px 10px;
  height: 36px;
  border-radius: 4px;
}

.size-md {
  font-size: 13px;
  padding: 8px 12px;
  height: 48px;
  border-radius: 6px;
}

.size-lg {
  font-size: 16px;
  padding: 10px 14px;
  height: 56px;
  border-radius: 8px;
}

/* Error and info text */
.error-text {
  color: #fd397a;
  font-style: italic;
  margin-top: 6px;
}

.info-text {
  color: #6c757d;
  font-size: 12px;
  margin-top: 4px;
}


.memlist-select-input-container {
  width: 100%; /* Ensure the container spans the full width */
  clear: both; /* Clear floats after the label and input fields */
}

.horizontal-layout {
  display: flex;
  width: 100%;
  align-items: center;
}

.horizontal1 {
  width: 50%;
  text-align: right;
  padding-right: 16px;
}

.horizontal2 {
  width: 50%;
}

.success1 {
  border: 1px solid #1BC5BD !important;
}

.failure1 {
  border: 1px solid #fd397a !important;
}

.required-asterisk {
  color: #00c900;
  margin-left: 2px;
}
</style>
